import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";

const BUYER_SECTIONS = [
  { k: "schedule",  label: "Sale Schedule" },
  { k: "hotels",    label: "Hotels & Lodging" },
  { k: "trucking",  label: "Trucking & Delivery" },
  { k: "bidding",   label: "Bidding" },
  { k: "insurance", label: "Insurance" },
  { k: "contact",   label: "Contact the Sale Office" },
];

const Lead = ({ children }) => (
  <p className="text-lg md:text-xl font-bold leading-snug" style={{ color: "var(--text-primary)" }}>{children}</p>
);
const Line = ({ children }) => (
  <p className="text-base md:text-lg leading-relaxed" style={{ color: "var(--text-secondary)" }}>{children}</p>
);
const Sub = ({ children }) => (
  <p className="text-base md:text-lg font-bold mt-6" style={{ color: "var(--text-primary)" }}>{children}</p>
);
const B = ({ children }) => <strong style={{ color: "var(--text-primary)" }}>{children}</strong>;

const STRUCTURED = {
  schedule: (
    <div className="space-y-1.5">
      <Lead>Monday, February 15, 2027  •  1:00 PM MT</Lead>
      <Line>Billings Livestock Commission</Line>
      <Sub>Sunday, February 14</Sub>
      <Line>Cattle viewing at BLC</Line>
      <Line><B>6:30 PM</B>  •  Bulls of the Big Sky Dinner at <B>Homewood Suites</B></Line>
      <Sub>Sale Day</Sub>
      <Line><B>9:00 AM–1:00 PM</B>  •  Cattle Viewing</Line>
      <Line>Lunch sponsored by Bulls of the Big Sky</Line>
      <Line><B>1:00 PM</B>  •  Sale</Line>
    </div>
  ),
  hotels: (
    <div className="space-y-1.5">
      <Lead>Homewood Suites</Lead>
      <Line>3420 Ember Lane, Billings, MT  •  <B>406-656-0525</B></Line>
      <Line>Block of rooms held under &ldquo;Bulls of the Big Sky&rdquo; through <B>February 5</B>.</Line>
      <Sub>Also nearby</Sub>
      <Line><B>Hampton Inn</B> — 5110 Southgate Dr  •  406-248-4949</Line>
      <Line><B>Holiday Inn Express</B> — 430 Cole St  •  406-259-8600</Line>
      <Line><B>Boot Hill Inn</B> — 242 E. Airport Rd  •  406-245-2000</Line>
      <p className="text-base md:text-lg mt-6 font-italic-serif" style={{ color: "var(--brass-deep)" }}>
        Book early — sale weekends fill fast.
      </p>
    </div>
  ),
  trucking: (
    <div className="space-y-4">
      <Lead>Scheduled bull delivery starts in late March or early April.</Lead>
      <Line>Bulls selling for at least <B>$5,000</B> will be delivered for free up to the first <B>500 miles</B> from Manhattan, Montana.</Line>
      <Line>After the sale, bulls are fed free of charge at <B>Veltkamp&apos;s Bull Development Facility</B> in Manhattan, Montana until <B>April 1</B>, or until scheduled delivery may be arranged — each bull receives a Breeding Soundness Exam prior to delivery.</Line>
      <Line>Bulls taken immediately after the sale receive a <B>$150 early-delivery discount</B>.</Line>
      <Line>See the Feeding and Delivery terms below for full details.</Line>
    </div>
  ),
  bidding: (
    <div className="space-y-4">
      <Lead>Bid in person at Billings Livestock Commission, by phone, or online through DV Auction.</Lead>
      <Line>If you plan to bid online, we encourage you to set up your free DV Auction account during business days before the sale.</Line>
      <Line>Want to bid by phone? Call the sale office anytime — even sale morning — at <B>208-267-2668</B> and we&apos;ll get you set up.</Line>
      <Line>Volume buyers purchasing <B>five or more bulls</B> receive a <B>5% rebate</B> on their total purchase following delivery and before <B>June 1</B>.</Line>
    </div>
  ),
  insurance: (
    <div className="space-y-4">
      <Lead>Insurance will be available on sale day and is recommended.</Lead>
      <Line>Bulls of the Big Sky encourage buyers to purchase insurance on the bulls for death or injury.</Line>
    </div>
  ),
  contact: (
    <div className="space-y-4">
      <Lead>Sale Office  •  <a href="tel:208-267-2668" className="link-brass">208-267-2668</a>  •  <a href="mailto:rymocattle@gmail.com" className="link-brass">rymocattle@gmail.com</a></Lead>
      <Line>427 Peaceful Way, Bonners Ferry, ID 83805</Line>
      <Line>Call us with any question — hotels, hauling, bidding, or a specific bull. We&apos;d rather take the call than have a buyer go home unsure.</Line>
    </div>
  ),
};

const TERMS = [
  { key: "warranty", label: "Bull Warranty",
    body: "All bulls sold are sold as guaranteed breeding sound for one breeding season, not to exceed ninety (90) days. This guarantee does not include loss of use due to abuse, neglect, illness due to poor nutrition or physical health, and/or acts of God. In the event a Buyer has any problem with a bull prior to or during the bull's first breeding season the Buyer shall immediately contact the breeder of the bull to report a claim. A statement of diagnosis from a competent veterinarian will be required with the submission of each claim. Bulls will be covered for unsoundness not caused by illness or foot rot. Seller reserves the right to demand return of the bull at the Seller's sole discretion, and/or request a second opinion from a veterinarian of Seller's choice. In the event of a valid claim submission, Buyer will be provided a replacement bull of comparable quality if Seller or any other Bulls of the Big Sky member has one available. In the event a replacement bull is not available, the Buyer will be given a full credit, minus salvage value, to be used in a future Bulls of the Big Sky sale. Bulls of the Big Sky members will not be held responsible, under any circumstance, for crediting any settlement amount exceeding the purchase price of a replacement bull. We are confident in our bulls, and we will work with any Buyer in good faith to correct any issues with a bull. In return, we ask the Buyers to use common sense and good management practices, providing adequate shelter and feed for young bulls, and keeping in mind the bull's age at turnout." },
  { key: "announcements", label: "Announcements",
    body: "Announcements made from the auction block during the sale take precedence over printed material in the catalog or other printed material associated with this sale. Anyone attending this sale attends at their own risk. Breeders and sale force will not be held responsible for any personal injury for attending this sale." },
  { key: "volume-discount", label: "Volume Buyer Discount",
    body: "Buyers purchasing 5 or more bulls will receive a 5% rebate on their total purchase following delivery and before June 1." },
  { key: "out-of-state", label: "Notice to Out of State Buyers",
    body: "Please know your state health requirements prior to sale day. If your state requires Trich testing on 12 months or younger, the bull(s) will not be able to leave Montana immediately after the sale and must return to Veltkamp's for testing. Though Bulls of the Big Sky pays for the cost of all regular health papers, the cost of Trich testing and other state specific health requirements will be the responsibility of the Buyer and will be billed to the Buyer." },
  { key: "canadian", label: "Notice to Canadian Buyers",
    body: "Bulls of the Big Sky will provide free delivery to the US side of Port of Entry at Sweetgrass, MT and pay for the cost of regular health papers. The Buyer is responsible for the cost of additional Canadian or Provincial Health Requirements." },
  { key: "feeding-delivery", label: "Feeding and Delivery",
    body: "After the sale, the bulls will be fed free of charge at Veltkamp's Bull Development Facility in Manhattan, Montana until April 1, 2026, or until scheduled delivery may be arranged. Following the sale and prior to bull delivery, each bull will receive a Breeding Soundness Exam. Buyers may anticipate scheduled bull delivery to start the last week of March or early April. If you have special delivery requests, please let us know at the time of the sale. If a Buyer wishes to keep their bull at Veltkamp's beyond April 1st, feed and freight will be the responsibility of the Buyer. Feed charges will accrue at $4.50 per head per day for Buyers that request their bulls remain at Veltkamp's facility after scheduled delivery times.",
    bullets: [
      "Early Delivery: Bulls taken by the Buyer immediately after the sale will receive a $150 discount off the purchase price of their bull. For all bulls requiring delivery prior to scheduled delivery times (in or around late March), the most economical trucking available may be arranged at Buyer's expense. Buyers of early delivery bulls must have a Breeding Soundness Exam performed prior to turnout for the Bull Warranty to remain in effect.",
      "Scheduled Delivery: Scheduled bull delivery starts in late March or early April. Bulls selling for at least $5,000 will be delivered for free up to the first 500 miles from Manhattan, Montana. All bulls selling for less than $5,000 will be delivered to the nearest central delivery point that is convenient to Seller. All bulls selling for at least $5,000 and requiring delivery over 500 miles from Manhattan, Montana, will be delivered to the nearest central delivery point convenient to Seller, or the most economical trucking available may be arranged at Buyer's expense.",
    ] },
  { key: "semen", label: "Retaining Semen Interest",
    body: "The bull buyer will receive full possession, 100% salvage value, and \u00be semen interest of any bull purchased in this sale, while the individual selling ranch (\u201cSeller\u201d) will retain \u00bc semen interest in any bull that it sells. Seller's retained semen interest is a royalty interest that entitles Seller to \u00bc of any revenue from the sale of that bull's semen. Seller's retained semen interest shall remain even if the bull, or any interest in the bull, is later sold to a third-party; and, the Buyer shall be solely responsible for notifying any later third-party buyer of the Seller's retained semen interest and for ensuring that Seller's retained semen interest is satisfied if semen is ever sold. Seller will not be responsible for any expenses directly or indirectly related to semen collection or promotion of a purchased bull, unless the Buyer and Seller agree otherwise in writing prior to semen collection. Seller's retained semen interest also includes the right of Seller to collect semen on the purchased bull at Seller's sole expense and at the Buyer's reasonable convenience." },
  { key: "association", label: "American Simmental Association",
    body: "All cattle sell under the Suggested Terms and Conditions of the American Simmental Association. EPDs are the most current BOLT at catalog print, conducted by IGS and the American Simmental Association. EPDs may change between print and the date registration papers are transferred." },
  { key: "bidding-terms", label: "Online Bidding & Sale Updates",
    body: "Bid live online at alliedgeneticresources.dvauction.com (Cattleusa.com is not available for sale day). All successful bidders will be invoiced from Bulls of the Big Sky. Sale order will be posted on bullsofthebigsky.com, DVAuction.com, Instagram and Facebook by Sunday evening." },
  { key: "health", label: "Health & Vaccinations",
    body: "Every bull has been vaccinated and boostered at weaning with BoviShield 5, Inforce, OneShot Ultra 8 (or a Bovi OneShot combo), Somnubac and Dectomax Injectable, and poured with Ivermectin in December. All bulls have been tested and are negative for BVD/PI3." },
];

export default function BuyerInfoPage() {
  const [info, setInfo] = useState(null);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    document.title = "Buyer Information & Sale Terms — Bulls of the Big Sky";
    (async () => {
      const [b, s] = await Promise.all([api.get("/buyer-info"), api.get("/site-settings")]);
      setInfo(b.data);
      setSettings(s.data);
    })();
  }, []);

  return (
    <div data-testid="buyer-info-page" className="min-h-screen" style={{ background: "var(--bg-primary)" }}>
      <SiteHeader />

      <section className="relative min-h-[62vh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://customer-assets-gfyr7b9c.emergentagent.net/job_import-hub-80/artifacts/55p3x4ic_20240219_130437.webp"
            alt="Buyers filling the seats around the auction ring"
            className="w-full h-full object-cover"
            style={{ objectPosition: "center 40%" }}
          />
        </div>
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative w-full max-w-[1400px] mx-auto px-6 md:px-12 pb-14 pt-40">
          <div className="eyebrow" style={{ color: "#E8D7A8" }}>Buyer Information</div>
          <h1
            className="font-display text-6xl md:text-8xl lg:text-9xl mt-4 leading-[0.95]"
            style={{ color: "#FFFFFF", textShadow: "0 4px 24px rgba(14,44,70,0.55)" }}
          >
            Everything you need.
          </h1>
          <p className="mt-8 max-w-3xl text-base md:text-lg" style={{ color: "rgba(255,255,255,0.88)" }}>
            You&apos;re viewing buyer information for the <strong style={{ color: "#E8D7A8" }}>Annual Sale — February 15, 2027</strong>.
            Logistics on top, sale terms below. Questions on any point? Call the sale office.
          </p>

          <div className="mt-8 flex gap-6 text-sm">
            <a href="#logistics" className="link-brass" style={{ color: "#E8D7A8" }}>Logistics</a>
            <a href="#terms" className="link-brass" style={{ color: "#E8D7A8" }}>Sale Terms</a>
          </div>
        </div>
      </section>

      {/* Sale picker — below the hero */}
      <div className="px-6 md:px-12 py-10 md:py-12">
        <div className="max-w-[1400px] mx-auto flex flex-wrap gap-3" data-testid="buyer-info-sale-picker">
          <span
            data-testid="picker-annual"
            className="px-8 py-4 text-base md:text-lg font-bold tracking-widest uppercase"
            style={{
              fontFamily: "'Big Shoulders Display', sans-serif",
              letterSpacing: "0.15em",
              background: "var(--hay)",
              color: "#0E2C46",
              border: "2px solid var(--hay)",
            }}
          >
            Annual Sale  ✓
          </span>
          <Link
            to="/buyer-info/legacy"
            data-testid="legacy-buyer-info-link"
            className="px-8 py-4 text-base md:text-lg font-bold tracking-widest uppercase transition-colors hover:bg-[#1E4D75] hover:text-white"
            style={{
              fontFamily: "'Big Shoulders Display', sans-serif",
              letterSpacing: "0.15em",
              border: "2px solid #1E4D75",
              color: "#1E4D75",
            }}
          >
            Legacy Sale →
          </Link>
        </div>
      </div>

      {/* Logistics */}
      <section id="logistics" className="px-6 md:px-12 pb-24 md:pb-32">
        <div className="max-w-[1400px] mx-auto">
          <div className="mb-16 md:mb-20">
            <h2 className="font-display text-4xl md:text-6xl leading-[0.95]" data-testid="buyer-info-heading">
              Annual Sale<br />Buyer Information
            </h2>
            <div className="font-italic-serif text-xl md:text-2xl mt-4" style={{ color: "var(--brass-deep)" }}>
              February 15, 2027  •  Billings, Montana
            </div>
          </div>
          <div className="space-y-16 md:space-y-20">
          {BUYER_SECTIONS.map((row, i) => (
            <div key={row.k} data-testid={`buyer-section-${row.k}`} className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-start">
              <div className="md:col-span-3">
                <div className="font-display text-7xl md:text-8xl" style={{ color: "rgba(30,77,117,0.4)" }}>
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div className="eyebrow mt-3">{row.label}</div>
              </div>
              <div className="md:col-span-9 md:pl-8 md:border-l" style={{ borderColor: "var(--border-subtle)" }}>
                {STRUCTURED[row.k]}
              </div>
            </div>
          ))}
          </div>
        </div>
      </section>

      {/* Sale Terms */}
      <section id="terms" className="px-6 md:px-12 pb-32 border-t pt-24" style={{ borderColor: "var(--border-subtle)" }}>
        <div className="max-w-[1400px] mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-end mb-16">
            <div className="md:col-span-7">
              <div className="eyebrow mb-3">About the Bulls</div>
              <h2 className="font-display text-5xl md:text-7xl leading-[0.95]">
                Sale terms &<br />conditions.
              </h2>
            </div>
            <div className="md:col-span-5">
              <p className="text-base md:text-lg" style={{ color: "var(--text-secondary)" }}>
                The same terms printed in every sale catalog. If anything is unclear, call us before
                bidding — we&apos;d rather take the call than have a buyer go home unsure.
              </p>
            </div>
          </div>

          <div className="space-y-12 md:space-y-14">
            {TERMS.map((row, i) => (
              <div key={row.key} data-testid={`terms-${row.key}`} className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 items-start">
                <div className="md:col-span-3">
                  <div className="font-display text-5xl md:text-6xl" style={{ color: "rgba(30,77,117,0.4)" }}>
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <div className="eyebrow mt-2">{row.label}</div>
                </div>
                <div className="md:col-span-9 md:pl-6 md:border-l" style={{ borderColor: "var(--border-subtle)" }}>
                  <p className="text-base md:text-lg leading-relaxed" style={{ color: "var(--text-primary)" }}>
                    {row.body}
                  </p>
                  {row.bullets && (
                    <ul className="mt-5 space-y-4 list-none">
                      {row.bullets.map((b) => (
                        <li key={b} className="flex gap-3 text-base md:text-lg leading-relaxed" style={{ color: "var(--text-primary)" }}>
                          <span aria-hidden style={{ color: "var(--brass)" }}>•</span>
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="border-t pt-10 mt-16" style={{ borderColor: "var(--border-subtle)" }}>
            <div className="eyebrow mb-6">Sale Day Contacts</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="sale-day-contacts">
              <div>
                <div className="font-display text-xl">Maureen Mai</div>
                <div className="text-sm mt-1" style={{ color: "var(--text-secondary)" }}>Catalog Requests & Sale Office</div>
                <a href="tel:208-267-2668" className="link-brass text-sm">208-267-2668</a>
              </div>
              <div>
                <div className="font-display text-xl">Marty Ropp</div>
                <div className="text-sm mt-1" style={{ color: "var(--text-secondary)" }}>Allied Genetic Resources</div>
                <a href="tel:406-581-7835" className="link-brass text-sm">406-581-7835</a>
              </div>
              <div>
                <div className="font-display text-xl">Corey Wilkins</div>
                <div className="text-sm mt-1" style={{ color: "var(--text-secondary)" }}>Allied Genetic Resources</div>
                <a href="tel:256-590-2487" className="link-brass text-sm">256-590-2487</a>
              </div>
              <div>
                <div className="font-display text-xl">Rocky Forseth</div>
                <div className="text-sm mt-1" style={{ color: "var(--text-secondary)" }}>Allied Genetic Resources</div>
                <a href="tel:406-590-7984" className="link-brass text-sm">406-590-7984</a>
              </div>
              <div>
                <div className="font-display text-xl">Jared Murnin</div>
                <div className="text-sm mt-1" style={{ color: "var(--text-secondary)" }}>Allied Genetic Resources</div>
                <a href="tel:406-321-1542" className="link-brass text-sm">406-321-1542</a>
              </div>
              <div>
                <div className="font-display text-xl">Jeff Thomas</div>
                <div className="text-sm mt-1" style={{ color: "var(--text-secondary)" }}>Sale Day Phone</div>
                <a href="tel:406-581-8859" className="link-brass text-sm">406-581-8859</a>
              </div>
              <div>
                <div className="font-display text-xl">Mike Stoltey</div>
                <div className="text-sm mt-1" style={{ color: "var(--text-secondary)" }}>Sale Day Phone</div>
                <a href="tel:208-699-2335" className="link-brass text-sm">208-699-2335</a>
              </div>
              <div>
                <div className="font-display text-xl">Billings Livestock Commission</div>
                <div className="text-sm mt-1" style={{ color: "var(--text-secondary)" }}>Sale Facility</div>
                <a href="tel:800-635-7364" className="link-brass text-sm">800-635-7364</a>
              </div>
            </div>
          </div>

          <div className="border-t pt-10 mt-16 text-sm" style={{ borderColor: "var(--border-subtle)", color: "var(--text-secondary)" }}>
            Sale Office  •  <a href="tel:208-267-2668" className="link-brass">208-267-2668</a>  •
            <a href="mailto:rymocattle@gmail.com" className="link-brass ml-2">rymocattle@gmail.com</a>
            <div className="mt-2">427 Peaceful Way, Bonners Ferry, ID 83805</div>
            <Link to="/" className="link-brass inline-block mt-4">← Back to home</Link>
          </div>
        </div>
      </section>

      <SiteFooter settings={settings} />
    </div>
  );
}
